import {useEffect, useState} from 'react';
import {Button, Form, Modal} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {DhcpServer} from '../../Types/routerForms';
import {IP_REGEX} from '../../Utils/random';

interface EditDhcpModalProps {
  show: boolean;
  onHide: () => void;
  initial: DhcpServer;
  networks: string[];
  onSave: (next: DhcpServer) => void;
}

const EditDhcpModal = ({
  show,
  onHide,
  initial,
  networks,
  onSave,
}: EditDhcpModalProps): JSX.Element => {
  const intl = useIntl();
  const [state, setState] = useState<DhcpServer>(initial);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (show) {
      setState(initial);
      setValidated(false);
    }
  }, [show, initial]);

  const isStartValid = IP_REGEX.test(state.rangeStart);
  const isEndValid = IP_REGEX.test(state.rangeEnd);
  const isWinsValid = state.wins === '' || IP_REGEX.test(state.wins);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidated(true);
    if (!isStartValid || !isEndValid || !isWinsValid) return;
    onSave(state);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.LAN.DHCP.EDIT_TITLE'})}
        </Modal.Title>
      </Modal.Header>
      <Form noValidate validated={validated} onSubmit={handleSubmit} id="editDhcpForm">
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.LAN.DHCP.NETWORK'})}
            </Form.Label>
            <Form.Select
              value={state.network}
              onChange={(e) => setState({...state, network: e.target.value})}
            >
              {networks.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="required">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.LAN.DHCP.RANGE_START'})}
            </Form.Label>
            <Form.Control
              type="text"
              value={state.rangeStart}
              onChange={(e) => setState({...state, rangeStart: e.target.value})}
              isInvalid={validated && !isStartValid}
              required
            />
            <Form.Control.Feedback type="invalid">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.IP'})}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="required">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.LAN.DHCP.RANGE_END'})}
            </Form.Label>
            <Form.Control
              type="text"
              value={state.rangeEnd}
              onChange={(e) => setState({...state, rangeEnd: e.target.value})}
              isInvalid={validated && !isEndValid}
              required
            />
            <Form.Control.Feedback type="invalid">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.IP'})}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.LAN.DHCP.WINS'})}
            </Form.Label>
            <Form.Control
              type="text"
              value={state.wins}
              onChange={(e) => setState({...state, wins: e.target.value})}
              placeholder="(optionnel)"
              isInvalid={validated && !isWinsValid}
            />
            <Form.Control.Feedback type="invalid">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.IP'})}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.LAN.DHCP.STATUS'})}
            </Form.Label>
            <Form.Select
              value={state.active ? 'on' : 'off'}
              onChange={(e) => setState({...state, active: e.target.value === 'on'})}
            >
              <option value="on">
                {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.LAN.DHCP.STATUS_ON'})}
              </option>
              <option value="off">
                {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.LAN.DHCP.STATUS_OFF'})}
              </option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {intl.formatMessage({id: 'COMMON.CANCEL'})}
          </Button>
          <Button variant="primary" type="submit" form="editDhcpForm">
            {intl.formatMessage({id: 'COMMON.SAVE'})}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditDhcpModal;
